// ── Contest display helpers (EX-PUB08) ──────────────────────────────────────

import { ApiError } from "@/lib/api";
import { formatNumber } from "@/lib/format-datetime";
import type {
  ContestPhase,
  ContestParticipantStatus,
  PublicContestHost,
  PublicContestHostOrganization,
  PublicContestSummary,
} from "../types";
import type { ContestDisplayZone } from "./display-zone";

/** Phase chips. The clock is the authority (`docs/29` §6.1); this only paints it. */
export const PHASE_CLASS: Readonly<Record<ContestPhase, string>> = {
  draft: "bg-neutral-100 text-neutral-600 border-neutral-200",
  scheduled: "bg-blue-50 text-blue-700 border-blue-200",
  running: "bg-emerald-50 text-emerald-700 border-emerald-200",
  frozen: "bg-sky-50 text-sky-700 border-sky-200",
  ended: "bg-amber-50 text-amber-700 border-amber-200",
  unfrozen: "bg-violet-50 text-violet-700 border-violet-200",
};

export const PARTICIPANT_STATUS_CLASS: Readonly<Record<ContestParticipantStatus, string>> = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  registered: "bg-emerald-50 text-emerald-700 border-emerald-200",
  blocked: "bg-neutral-100 text-neutral-600 border-neutral-200",
  disqualified: "bg-red-50 text-red-700 border-red-200",
};

/**
 * Stable backend error codes → keys in the `contest.errors` namespace.
 *
 * Every one of these is a refusal the server has already decided
 * (`docs/29` §5.2). The client renders the reason it was given and never
 * computes its own: a UI that guessed "register first" at a blocked contestant
 * would send them to support with the wrong question.
 */
const CODE_TO_ERROR_KEY: Readonly<Record<string, string>> = {
  // `EX-PKG02`: a profile or a stream the contest's stamp does not carry. The
  // console rail (2026-09-18) locks the control before this is reached; the
  // refusal is what a stale form or a direct API call still meets.
  CONTEST_CAPABILITY_NOT_INCLUDED: "capabilityNotIncluded",
  // `EX-EDO01`: a staff member has to let this contestant back in first.
  CONTEST_EDITOR_HOLD: "editorHold",
  // `EX-VEN01`: an issued login submits and runs only from the venue's networks.
  NETWORK_NOT_ALLOWED: "venueOutside",
  CONTEST_NOT_STARTED: "notStarted",
  CONTEST_ENDED: "ended",
  CONTEST_NOT_REGISTERED: "notRegistered",
  CONTEST_REGISTRATION_PENDING: "registrationPending",
  CONTEST_PARTICIPANT_BLOCKED: "participantBlocked",
  CONTEST_PARTICIPANT_DISQUALIFIED: "participantDisqualified",
  CONTEST_TEAM_REQUIRED: "teamRequired",
  CONTEST_PASSPHRASE_INVALID: "passphraseInvalid",
  CONTEST_INVITE_ONLY: "inviteOnly",
  // `EX-RUL01`, `docs/29` §5.1. The client also disables the register button
  // on this itself; the server refusal is what a direct API call still meets.
  CONTEST_RULES_NOT_ACCEPTED: "rulesNotAccepted",
  // `EX-ONS02`: the Print button's three refusals.
  CONTEST_SEAT_REQUIRED: "seatRequired",
  CONTEST_PRINT_LIMIT: "printLimit",
  CONTEST_PRINT_TRANSITION_INVALID: "printTransitionInvalid",
  // `EX-ONS03`: the runner's double tap, or another runner's earlier one.
  CONTEST_BALLOON_ALREADY_DELIVERED: "balloonAlreadyDelivered",
  // `EX-PUB09`. The cap lives on the owner's plan; the contestant is told the
  // contest is full, never to upgrade — the person who could is not them.
  CONTEST_REGISTRATION_FULL: "registrationFull",
  // `EX-REG01`: the organizer's registration questions and their answers.
  CONTEST_REGISTRATION_ANSWERS_REQUIRED: "registrationAnswersRequired",
  CONTEST_REGISTRATION_ANSWER_INVALID: "registrationAnswerInvalid",
  CONTEST_REGISTRATION_ANSWERS_LOCKED: "registrationAnswersLocked",
  CONTEST_REGISTRATION_QUESTIONS_LOCKED: "registrationQuestionsLocked",
  CONTEST_REGISTRATION_QUESTIONS_LIMIT: "registrationQuestionsLimit",
  CONTEST_REGISTRATION_QUESTION_INVALID: "registrationQuestionInvalid",
  CONTEST_SEAT_LIMIT_EXCEEDED: "seatLimitExceeded",
  CONTEST_COURSE_REQUIRED: "courseRequired",
  CONTEST_ACCESS_DENIED: "accessDenied",
  // `EX-WSP01`: the organizer asked for a workspace replay while the round runs.
  CONTEST_NOT_ENDED: "notEnded",
  CONTEST_ALREADY_STARTED: "alreadyStarted",
  // `EX-STF01`, `docs/29` §5.1: staff never compete. A 409, because nothing
  // is missing; the owner removing them from staff is the only way out.
  CONTEST_STAFF_CANNOT_PARTICIPATE: "staffCannotParticipate",
  // `EX-OFF01`, ADR-0099: an operator's account and a rated contest. A 409 in the staff shape.
  CONTEST_OPERATOR_CANNOT_COMPETE: "operatorCannotCompete",
  CONTEST_PROBLEM_NOT_ATTACHABLE: "problemNotAttachable",
  // `EX-SFX02`: the save's other three refusals, which the rows normally say
  // first; these are for a save that raced a change made somewhere else.
  CONTEST_PROBLEM_UNVALIDATED: "problemUnvalidated",
  CONTEST_PROBLEM_NO_EXPECTED_TIME: "problemNoExpectedTime",
  CONTEST_PROBLEM_NO_POINTS: "problemNoPoints",
  // `EX-PUB07`. Each of these is a rule from `docs/29` §7 that the server
  // enforces and this client only renders: what may be rated, and when.
  CONTEST_NOT_RATED: "notRated",
  CONTEST_TEAM_UNRATED: "teamUnrated",
  CONTEST_COURSE_ALIGNED_UNRATED: "courseAlignedUnrated",
  // `EX-DAY04`: a warm-up is never rated, and only its main contest's field enters it.
  CONTEST_WARM_UP_UNRATED: "warmUpUnrated",
  CONTEST_WARM_UP_ENTRANTS_ONLY: "warmUpEntrantsOnly",
  CONTEST_RATING_NOT_UNFROZEN: "ratingNotUnfrozen",
  CONTEST_RATING_NOT_APPLIED: "ratingNotApplied",
  // `EX-RRQ01`, `docs/29` §7.1. The two §7.4 refusals above are reused rather
  // than duplicated, because the request route reaches them through the same
  // `assertRatable` the promote does: one predicate, one message.
  CONTEST_ALREADY_RATED: "alreadyRated",
  CONTEST_RATED_REQUEST_OPEN: "ratedRequestOpen",
  CONTEST_RATED_REQUEST_DECIDED: "ratedRequestDecided",
  CONTEST_RATED_REQUEST_ABSENT: "ratedRequestAbsent",
  // `EX-DIV01`, Wave 29 decision 3: a band that is closed to this rating (Div. 1).
  CONTEST_DIVISION_CLOSED: "divisionClosed",
  // `EX-DIV04`, `docs/29` §8.1: a division of a round. The rating sends each
  // person to one division, and one person holds one place in the round.
  CONTEST_ROUND_OTHER_DIVISION: "roundOtherDivision",
  CONTEST_ROUND_ONE_DIVISION: "roundOneDivision",
  // `EX-CLA01`: the Clarification Assistant's refusals, in the reader's language.
  CONTEST_CLARIFICATION_CREDITS_SHORT: "clarificationCreditsShort",
  CONTEST_CLARIFICATION_ASSIST_CLOSED: "clarificationAssistClosed",
  CONTEST_CLARIFICATION_NOTHING_TO_GROUP: "clarificationNothingToGroup",
  CONTEST_CLARIFICATION_DRAFT_UNCHECKED: "clarificationDraftUnchecked",
  CONTEST_CLARIFICATION_GROUP_MIXED: "clarificationGroupMixed",
};

export function contestErrorKey(err: unknown): string | null {
  if (!(err instanceof ApiError) || !err.code) return null;
  return CODE_TO_ERROR_KEY[err.code] ?? null;
}

export type Remaining = {
  total: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

/** Milliseconds until `iso`, split for display. `null` when there is no target. */
export function remainingUntil(iso: string | null, now: number): Remaining | null {
  if (!iso) return null;
  const total = Math.max(0, new Date(iso).getTime() - now);
  return {
    total,
    days: Math.floor(total / 86_400_000),
    hours: Math.floor(total / 3_600_000) % 24,
    minutes: Math.floor(total / 60_000) % 60,
    seconds: Math.floor(total / 1000) % 60,
  };
}

export function formatRemaining(remaining: Remaining): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return remaining.days > 0
    ? `${remaining.days}d ${pad(remaining.hours)}:${pad(remaining.minutes)}:${pad(remaining.seconds)}`
    : `${pad(remaining.hours)}:${pad(remaining.minutes)}:${pad(remaining.seconds)}`;
}

const ZONE_LABEL: Readonly<Record<"en" | "bn", Readonly<Record<ContestDisplayZone, string>>>> = {
  en: { "Asia/Dhaka": "Bangladesh time", UTC: "UTC" },
  bn: { "Asia/Dhaka": "বাংলাদেশ সময়", UTC: "UTC" },
};

/**
 * A contest time on the clock `zone` names, with that zone printed after it
 * (`EX-PUB18`). `zone` is required so no caller can fall back to "whatever
 * clock this code happens to run on": that fallback is how the `/judge` round
 * card printed UTC from the server while the strip under it printed Dhaka time
 * from the browser, six hours and a calendar day apart for one contest.
 * Client components pass `useContestDisplayZone()`; a Server Component renders
 * `ContestDateTime` instead.
 */
export function formatDateTime(iso: string | null, locale: string, zone: ContestDisplayZone): string {
  if (!iso) return "-";
  return `${formatDateTimeWithoutZone(iso, locale, zone)} ${contestZoneLabel(locale, zone)}`;
}

/**
 * `EX-PUB22`: the same time without the zone, for a line that prints the zone
 * in a weight of its own (the contest page's head). The caller still prints
 * {@link contestZoneLabel} beside it; the time alone never names a clock.
 */
export function formatDateTimeWithoutZone(iso: string, locale: string, zone: ContestDisplayZone): string {
  return new Date(iso).toLocaleString(locale === "bn" ? "bn-BD" : "en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: zone,
  });
}

/**
 * `EX-CPG02`: the time of day alone, `23:00` (`২৩:০০` on `/bn`), for a
 * timeline row that falls on the same day as the start printed above it.
 */
export function formatClockTime(iso: string, locale: string, zone: ContestDisplayZone): string {
  return new Date(iso).toLocaleString(locale === "bn" ? "bn-BD" : "en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: zone,
  });
}

/** `EX-CPG02`: whether two instants fall on one calendar day on the clock `zone` names. */
export function sameDayInZone(a: string, b: string, zone: ContestDisplayZone): boolean {
  const day = (iso: string) => new Date(iso).toLocaleDateString("en-CA", { timeZone: zone });
  return day(a) === day(b);
}

/** `EX-PUB22`: the zone's own name, for a note that says which clock a page uses. */
export function contestZoneLabel(locale: string, zone: ContestDisplayZone): string {
  return ZONE_LABEL[locale === "bn" ? "bn" : "en"][zone];
}

/**
 * `EX-PUB22`: the directory card's date block (month, day, weekday and time),
 * on the same clock as {@link formatDateTime}.
 */
export function dateBlockParts(
  iso: string | null,
  locale: string,
  zone: ContestDisplayZone,
): { month: string; day: string; weekdayTime: string } | null {
  if (!iso) return null;
  const date = new Date(iso);
  const tag = locale === "bn" ? "bn-BD" : "en-GB";
  return {
    month: date.toLocaleString(tag, { month: "short", timeZone: zone }),
    day: date.toLocaleString(tag, { day: "numeric", timeZone: zone }),
    weekdayTime: date.toLocaleString(tag, {
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: zone,
    }),
  };
}

/**
 * `EX-PUB22`: a minute of the contest as hours and minutes, `0:21` or `25:07`,
 * the way a scoreboard prints when a problem was solved. Hours are not capped
 * at a day, because a two-day contest's minute 1,500 is `25:00`, not `1:00`.
 */
export function formatContestMinute(minute: number): string {
  const whole = Math.max(0, Math.floor(minute));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

/**
 * The length's units per language. English keeps the compact `2h 30m` the
 * contest pages have always printed; Bangla says the words aloud, the way the
 * other duration strings in `messages/bn.json` do (`২ ঘণ্টা ৩০ মিনিট`), because
 * a Latin `h` beside Bangla digits reads as neither language.
 */
const DURATION_UNITS: Readonly<Record<"en" | "bn", { hours: string; minutes: string }>> = {
  en: { hours: "h", minutes: "m" },
  bn: { hours: " ঘণ্টা", minutes: " মিনিট" },
};

/**
 * `2h 30m` (`২ ঘণ্টা ৩০ মিনিট` on `/bn`), from the two ends of the window, with
 * the reader's digits (`docs/11` §2). Empty when either end is unset.
 */
export function formatDuration(startAt: string | null, endAt: string | null, locale: string): string {
  if (!startAt || !endAt) return "";
  const minutes = Math.max(
    0,
    Math.round((new Date(endAt).getTime() - new Date(startAt).getTime()) / 60_000),
  );
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const units = DURATION_UNITS[locale === "bn" ? "bn" : "en"];
  const h = `${formatNumber(hours, locale)}${units.hours}`;
  const m = `${formatNumber(rest, locale)}${units.minutes}`;
  if (hours === 0) return m;
  return rest === 0 ? h : `${h} ${m}`;
}

/**
 * A `datetime-local` value from an ISO string, in the browser's own zone.
 *
 * The organizer sets a window in the timezone they are standing in; the wire is
 * UTC either way, and doing the conversion by hand here is what keeps a form
 * that reads "10:00" from scheduling 04:00.
 */
export function toLocalInputValue(iso: string | null): string {
  if (!iso) return "";
  const date = new Date(iso);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

/**
 * `EX-PUB19`: the name a community contest's host line prints, the display
 * name or, when that is blank, the username. `null` when the payload names
 * nobody: a Progsity round, an owner the credit rule does not name, or an API
 * older than the field.
 */
export function contestHostName(host: PublicContestHost | null | undefined): string | null {
  return host ? host.name.trim() || host.username || null : null;
}

/**
 * `EX-HST03`: the host organization's name in the reader's language, the
 * English one when no Bangla name was given. `null` when there is none.
 */
export function contestHostOrganizationName(
  org: PublicContestHostOrganization | null | undefined,
  locale: string,
): string | null {
  if (!org) return null;
  return (locale === "bn" ? org.name_bn?.trim() : "") || org.name_en.trim() || null;
}

/** What a host line prints: an organization (with its logo) or the owner as a person. */
export type ContestHostLine =
  | { kind: "organization"; name: string; logoImageId: string | null }
  | { kind: "person"; name: string; person: PublicContestHost };

/**
 * `EX-HST03`, `docs/29` §5: the one host line every surface prints. An
 * organization in place of the owner's name; on a Progsity round, beside
 * Progsity as co-host ("Progsity × DIU", the brand in both languages); the
 * owner otherwise (`EX-PUB19`); `null` when the payload names nobody.
 */
export function contestHostLine(
  contest: Pick<PublicContestSummary, "official" | "host" | "hostOrganization">,
  locale: string,
): ContestHostLine | null {
  const organization = contestHostOrganizationName(contest.hostOrganization, locale);
  if (organization) {
    return {
      kind: "organization",
      name: contest.official ? `Progsity × ${organization}` : organization,
      logoImageId: contest.hostOrganization?.logoImageId ?? null,
    };
  }
  const person = contestHostName(contest.host);
  return person && contest.host ? { kind: "person", name: person, person: contest.host } : null;
}

export function fromLocalInputValue(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/**
 * `EX-DAY03`: the two contest clocks as offsets from UTC. Both are fixed
 * (Bangladesh has kept no daylight saving since 2009), which is what lets a
 * typed time be read on the display clock with plain arithmetic.
 */
const ZONE_OFFSET_MINUTES: Record<ContestDisplayZone, number> = { "Asia/Dhaka": 360, UTC: 0 };

/**
 * `EX-DAY03`: a `datetime-local` value on the contest's display clock rather
 * than the device's, so a visitor abroad types the UTC they read everywhere
 * else on the page, and the zone printed beside the field is the truth.
 */
export function toZoneInputValue(iso: string | null, zone: ContestDisplayZone): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return new Date(date.getTime() + ZONE_OFFSET_MINUTES[zone] * 60_000).toISOString().slice(0, 16);
}

/** `EX-DAY03`: the ISO instant a `datetime-local` value names on `zone`'s clock, or `null`. */
export function fromZoneInputValue(value: string, zone: ContestDisplayZone): string | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return null;
  const asUtc = Date.parse(`${value}:00.000Z`);
  if (Number.isNaN(asUtc)) return null;
  return new Date(asUtc - ZONE_OFFSET_MINUTES[zone] * 60_000).toISOString();
}
